import { MetricName, MetricCategory, AlertStatus } from '../types/metrics';

// ─── Metric Metadata ──────────────────────────────────────────────────────────
export const METRIC_META: Record<MetricName, {
  label: string;
  category: MetricCategory;
  unit: string;
  description: string;
}> = {
  // Sprint Health
  velocity:             { label: 'Velocity',            category: 'sprint_health', unit: 'points', description: 'Story points completed per sprint' },
  commitment_accuracy:  { label: 'Commitment Accuracy', category: 'sprint_health', unit: '%',      description: 'Delivered SP vs committed SP' },
  scope_creep_rate:     { label: 'Scope Creep Rate',    category: 'sprint_health', unit: '%',      description: 'Story points added mid-sprint as % of original commitment' },
  carryover_rate:       { label: 'Carryover Rate',      category: 'sprint_health', unit: '%',      description: 'Tickets not completed and carried to next sprint' },
  // Quality
  bugs_fixed:           { label: 'Bugs Fixed',          category: 'quality', unit: 'count', description: 'Bug tickets closed in the sprint' },
  bug_injection_rate:   { label: 'Bug Injection Rate',  category: 'quality', unit: 'count', description: 'New bug tickets created during the sprint' },
  defect_escape_rate:   { label: 'Defect Escape Rate',  category: 'quality', unit: '%',    description: 'Production bugs as % of all bugs found (requires prod-defect label)' },
  reopened_ticket_rate: { label: 'Reopened Rate',        category: 'quality', unit: '%',    description: 'Tickets reopened after being marked Done, within the sprint' },
  // Flow — Jira changelog derived
  cycle_time_p50:           { label: 'Cycle Time (P50)',          category: 'flow', unit: 'days', description: 'Median days from In Progress → Done across completed tickets' },
  cycle_time_p85:           { label: 'Cycle Time (P85)',          category: 'flow', unit: 'days', description: '85th percentile cycle time — shows your slowest typical ticket' },
  time_in_review_p50:       { label: 'Time in Review (P50)',      category: 'flow', unit: 'days', description: 'Median days tickets spent in the In Review status' },
  time_in_review_p85:       { label: 'Time in Review (P85)',      category: 'flow', unit: 'days', description: '85th percentile time in review' },
  pr_review_time_p50:       { label: 'PR Review Time (P50)',      category: 'flow', unit: 'hours', description: 'Median hours from PR creation to merge' },
  pr_review_time_p85:       { label: 'PR Review Time (P85)',      category: 'flow', unit: 'hours', description: '85th percentile hours from PR creation to merge' },
  pr_size_median:           { label: 'PR Size (Median)',          category: 'flow', unit: 'lines', description: 'Median lines changed per pull request' },
  pr_review_participation:  { label: 'PR Review Participation',  category: 'flow', unit: 'count', description: 'Average number of reviewers per pull request' },
  blocked_time_median:      { label: 'Blocked Time (Median)',     category: 'flow', unit: 'hours', description: 'Median hours tickets spend in Blocked status' },
  // Team & Capacity
  throughput:           { label: 'Throughput',       category: 'team_capacity', unit: 'tickets', description: 'Total tickets completed regardless of size' },
  unplanned_work_pct:   { label: 'Unplanned Work %', category: 'team_capacity', unit: '%',       description: 'Unplanned SP as % of delivered SP (requires unplanned label)' },
};

// ─── Alert Thresholds ─────────────────────────────────────────────────────────
export const ALERT_THRESHOLDS: Partial<Record<MetricName, {
  green: (v: number) => boolean;
  amber: (v: number) => boolean;
  red:   (v: number) => boolean;
}>> = {
  commitment_accuracy: {
    green: (v) => v >= 70 && v <= 100,
    amber: (v) => (v >= 55 && v < 70) || (v > 100 && v <= 125),
    red:   (v) => v < 55 || v > 125,
  },
  carryover_rate: {
    green: (v) => v < 15,
    amber: (v) => v >= 15 && v < 30,
    red:   (v) => v >= 30,
  },
  defect_escape_rate: {
    green: (v) => v < 15,
    amber: (v) => v >= 15 && v < 35,
    red:   (v) => v >= 35,
  },
  reopened_ticket_rate: {
    green: (v) => v < 5,
    amber: (v) => v >= 5 && v < 15,
    red:   (v) => v >= 15,
  },
  cycle_time_p50: {
    green: (v) => v < 1,
    amber: (v) => v >= 1 && v < 3,
    red:   (v) => v >= 3,
  },
  cycle_time_p85: {
    green: (v) => v < 3,
    amber: (v) => v >= 3 && v < 5,
    red:   (v) => v >= 5,
  },
  time_in_review_p50: {
    green: (v) => v < 1,
    amber: (v) => v >= 1 && v < 3,
    red:   (v) => v >= 3,
  },
  time_in_review_p85: {
    green: (v) => v < 3,
    amber: (v) => v >= 3 && v < 5,
    red:   (v) => v >= 5,
  },
  bugs_fixed: {
    green: (v) => v <= 3,
    amber: (v) => v > 3 && v <= 8,
    red:   (v) => v > 8,
  },
  bug_injection_rate: {
    green: (v) => v < 10,
    amber: (v) => v >= 10 && v < 25,
    red:   (v) => v >= 25,
  },
  unplanned_work_pct: {
    green: (v) => v < 15,
    amber: (v) => v >= 15 && v < 30,
    red:   (v) => v >= 30,
  },
  scope_creep_rate: {
    green: (v) => v < 10,
    amber: (v) => v >= 10 && v < 25,
    red:   (v) => v >= 25,
  },
};

export function getAlertStatus(metricName: MetricName, value: number | null): AlertStatus {
  if (value === null) return 'unknown';
  const threshold = ALERT_THRESHOLDS[metricName];
  if (!threshold) return 'unknown';
  if (threshold.red(value)) return 'red';
  if (threshold.amber(value)) return 'amber';
  return 'green';
}

export const METRIC_CATEGORIES: Record<MetricCategory, MetricName[]> = {
  sprint_health: ['velocity', 'commitment_accuracy', 'scope_creep_rate', 'carryover_rate'],
  quality:       ['bugs_fixed', 'bug_injection_rate', 'defect_escape_rate', 'reopened_ticket_rate'],
  flow:          ['cycle_time_p50', 'cycle_time_p85', 'time_in_review_p50', 'time_in_review_p85',
                  'pr_review_time_p50', 'pr_review_time_p85', 'pr_size_median', 'pr_review_participation', 'blocked_time_median'],
  team_capacity: ['throughput', 'unplanned_work_pct'],
  capacity:      ['throughput', 'unplanned_work_pct'],
};